import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, KeyRound, Loader2 } from 'lucide-react';
import { api, fmtDate, formatApiError } from '@/lib/api';
import { USERS } from '@/constants/testIds';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
    Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
    AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

const empty = { name: '', email: '', password: '', role: 'user', status: 'active' };

export default function UsersPage() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(empty);
    const [saving, setSaving] = useState(false);
    const [resetOpen, setResetOpen] = useState(null); // user id
    const [newPass, setNewPass] = useState('');

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get('/users');
            setUsers(data);
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null); setForm(empty); setOpen(true);
    };
    const openEdit = (u) => {
        setEditing(u);
        setForm({ name: u.name || '', email: u.email, password: '', role: u.role, status: u.status });
        setOpen(true);
    };
    const submit = async () => {
        setSaving(true);
        try {
            if (editing) {
                const payload = { name: form.name, email: form.email, role: form.role, status: form.status };
                await api.put(`/users/${editing.id}`, payload);
                toast.success('User updated');
            } else {
                await api.post('/users', form);
                toast.success('User created');
            }
            setOpen(false); load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setSaving(false); }
    };
    const del = async (id) => {
        try { await api.delete(`/users/${id}`); toast.success('Deleted'); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const resetPw = async () => {
        try {
            await api.post(`/users/${resetOpen}/reset-password`, { password: newPass });
            toast.success('Password reset');
            setResetOpen(null); setNewPass('');
        } catch (e) { toast.error(formatApiError(e)); }
    };

    return (
        <div data-testid={USERS.root} className="space-y-6">
            <div className="flex items-end justify-between flex-wrap gap-4">
                <div>
                    <h1 className="font-display text-4xl font-black tracking-tight mt-1">Users</h1>
                    <p className="text-sm text-muted-foreground mt-2">
                        Manage administrators and read-only users of the panel.
                    </p>
                </div>
                <Dialog open={open} onOpenChange={setOpen}>
                    <DialogTrigger asChild>
                        <Button
                            onClick={openCreate}
                            data-testid={USERS.createButton}
                            className="rounded-sm bg-primary hover:bg-primary/90"
                        >
                            <Plus size={16} className="mr-2" /> New user
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-popover border-border rounded-sm">
                        <DialogHeader>
                            <DialogTitle className="font-display">
                                {editing ? 'Edit user' : 'Create user'}
                            </DialogTitle>
                        </DialogHeader>
                        <div className="grid gap-4 py-2">
                            <div className="space-y-2">
                                <Label className="text-sm">Name</Label>
                                <Input
                                    value={form.name}
                                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                                    data-testid={USERS.formName}
                                    className="rounded-sm"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-sm">Email</Label>
                                <Input
                                    type="email"
                                    value={form.email}
                                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                                    data-testid={USERS.formEmail}
                                    className="rounded-sm"
                                />
                            </div>
                            {!editing && (
                                <div className="space-y-2">
                                    <Label className="text-sm">Password</Label>
                                    <Input
                                        type="password"
                                        value={form.password}
                                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                                        data-testid={USERS.formPassword}
                                        className="rounded-sm"
                                    />
                                </div>
                            )}
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2">
                                    <Label className="text-sm">Role</Label>
                                    <Select
                                        value={form.role}
                                        onValueChange={(v) => setForm({ ...form, role: v })}
                                    >
                                        <SelectTrigger data-testid={USERS.formRole} className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-popover border-border">
                                            <SelectItem value="user">User</SelectItem>
                                            <SelectItem value="admin">Admin</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <Label className="text-sm">Status</Label>
                                    <Select
                                        value={form.status}
                                        onValueChange={(v) => setForm({ ...form, status: v })}
                                    >
                                        <SelectTrigger data-testid={USERS.formStatus} className="rounded-sm">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent className="bg-popover border-border">
                                            <SelectItem value="active">Active</SelectItem>
                                            <SelectItem value="inactive">Inactive</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                onClick={submit}
                                disabled={saving}
                                data-testid={USERS.formSubmit}
                                className="rounded-sm"
                            >
                                {saving && <Loader2 className="animate-spin mr-2" size={16} />}
                                {editing ? 'Save changes' : 'Create user'}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            </div>

            <div className="rounded-sm border border-border bg-card overflow-x-auto">
                <table className="w-full text-sm table-tech">
                    <thead>
                        <tr className="border-b border-border">
                            <th className="text-left px-4 py-3">Name</th>
                            <th className="text-left px-4 py-3">Email</th>
                            <th className="text-left px-4 py-3">Role</th>
                            <th className="text-left px-4 py-3">Status</th>
                            <th className="text-left px-4 py-3">Spaces</th>
                            <th className="text-left px-4 py-3">Last login</th>
                            <th className="text-right px-4 py-3">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">Loading…</td></tr>
                        ) : users.length === 0 ? (
                            <tr><td colSpan={7} className="text-center p-8 text-muted-foreground">No users yet</td></tr>
                        ) : users.map((u) => (
                            <tr
                                key={u.id}
                                data-testid={USERS.row(u.id)}
                                className="border-b border-border last:border-0 row-hover hover:bg-muted/40"
                            >
                                <td className="px-4 py-3">{u.name || <span className="text-muted-foreground">—</span>}</td>
                                <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                                <td className="px-4 py-3">
                                    <Badge
                                        variant="outline"
                                        className={`rounded-sm text-xs ${
                                            u.role === 'admin' ? 'border-primary/60 text-primary' : ''
                                        }`}
                                    >
                                        {u.role}
                                    </Badge>
                                </td>
                                <td className="px-4 py-3">
                                    <span
                                        className={`inline-flex items-center gap-1.5 text-xs ${
                                            u.status === 'active' ? 'text-emerald-500' : 'text-muted-foreground'
                                        }`}
                                    >
                                        <span
                                            className={`h-1.5 w-1.5 rounded-full ${
                                                u.status === 'active' ? 'bg-emerald-500' : 'bg-muted-foreground'
                                            }`}
                                        />
                                        {u.status}
                                    </span>
                                </td>
                                <td className="px-4 py-3">{u.assigned_spaces_count}</td>
                                <td className="px-4 py-3 text-xs text-muted-foreground">{fmtDate(u.last_login)}</td>
                                <td className="px-4 py-3">
                                    <div className="flex justify-end gap-1">
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-sm h-8 w-8"
                                            onClick={() => openEdit(u)}
                                            data-testid={USERS.rowEdit(u.id)}
                                            aria-label="Edit"
                                        >
                                            <Pencil size={14} />
                                        </Button>
                                        <Button
                                            variant="ghost"
                                            size="icon"
                                            className="rounded-sm h-8 w-8"
                                            onClick={() => { setResetOpen(u.id); setNewPass(''); }}
                                            data-testid={USERS.rowReset(u.id)}
                                            aria-label="Reset password"
                                        >
                                            <KeyRound size={14} />
                                        </Button>
                                        <AlertDialog>
                                            <AlertDialogTrigger asChild>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="rounded-sm h-8 w-8 text-destructive"
                                                    data-testid={USERS.rowDelete(u.id)}
                                                    aria-label="Delete"
                                                >
                                                    <Trash2 size={14} />
                                                </Button>
                                            </AlertDialogTrigger>
                                            <AlertDialogContent className="bg-popover border-border rounded-sm">
                                                <AlertDialogHeader>
                                                    <AlertDialogTitle>Delete user?</AlertDialogTitle>
                                                    <AlertDialogDescription>
                                                        This removes the user and all their space assignments. Cannot be undone.
                                                    </AlertDialogDescription>
                                                </AlertDialogHeader>
                                                <AlertDialogFooter>
                                                    <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
                                                    <AlertDialogAction
                                                        onClick={() => del(u.id)}
                                                        className="rounded-sm bg-destructive text-destructive-foreground"
                                                    >
                                                        Delete
                                                    </AlertDialogAction>
                                                </AlertDialogFooter>
                                            </AlertDialogContent>
                                        </AlertDialog>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!resetOpen} onOpenChange={(v) => !v && setResetOpen(null)}>
                <DialogContent className="bg-popover border-border rounded-sm">
                    <DialogHeader>
                        <DialogTitle className="font-display">Reset password</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-2 py-2">
                        <Label className="text-sm">New password</Label>
                        <Input
                            type="password"
                            value={newPass}
                            onChange={(e) => setNewPass(e.target.value)}
                            className="rounded-sm"
                        />
                    </div>
                    <DialogFooter>
                        <Button onClick={resetPw} className="rounded-sm" disabled={newPass.length < 6}>
                            Update password
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
